'use client';

import { useState } from 'react';

type Evento = {
  id: number;
  titulo: string;
  descricao: string;
  tipo: 'jogo' | 'evento' | 'viagem';
  data_evento: string;
  local: string;
  banner_url: string | null;
  galeria: string | null;
};

const TIPO_COLOR: Record<string, string> = {
  jogo: '#6b1122',
  evento: '#006437',
  viagem: '#c8860a',
};
const TIPO_LABEL: Record<string, string> = {
  jogo: 'Jogo',
  evento: 'Evento',
  viagem: 'Viagem',
};

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

function getEventosDoDia(eventos: Evento[], ano: number, mes: number, dia: number) {
  return eventos.filter(e => {
    const d = new Date(e.data_evento);
    return d.getFullYear() === ano && d.getMonth() === mes && d.getDate() === dia;
  });
}

function buildCalendarDays(ano: number, mes: number) {
  const primeiroDia = new Date(ano, mes, 1).getDay();
  const totalDias = new Date(ano, mes + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < primeiroDia; i++) cells.push(null);
  for (let d = 1; d <= totalDias; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function EventosView({ eventos }: { eventos: Evento[] }) {
  const hoje = new Date();
  const [view, setView] = useState<'cards' | 'calendar'>('cards');
  const [calMes, setCalMes] = useState(hoje.getMonth());
  const [calAno, setCalAno] = useState(hoje.getFullYear());
  const [diaSelecionado, setDiaSelecionado] = useState<number | null>(null);

  const cells = buildCalendarDays(calAno, calMes);

  function prevMes() {
    if (calMes === 0) { setCalMes(11); setCalAno(a => a - 1); }
    else setCalMes(m => m - 1);
    setDiaSelecionado(null);
  }
  function nextMes() {
    if (calMes === 11) { setCalMes(0); setCalAno(a => a + 1); }
    else setCalMes(m => m + 1);
    setDiaSelecionado(null);
  }

  const eventosDoDiaSelecionado = diaSelecionado
    ? getEventosDoDia(eventos, calAno, calMes, diaSelecionado)
    : [];

  return (
    <>
      <style>{`
        .ev-toggle-bar {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 8px; margin-bottom: 28px;
        }
        .ev-toggle-btn {
          display: flex; align-items: center; gap: 7px;
          padding: 9px 18px; border-radius: 6px; border: 2px solid #ddd;
          font-size: 0.78rem; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.06em; cursor: pointer; transition: all 0.2s;
          background: #fff; color: #6b1122;
          font-family: 'Montserrat', sans-serif;
        }
        .ev-toggle-btn.active {
          background: #6b1122; color: #fff; border-color: #6b1122;
        }
        .ev-toggle-btn:not(.active):hover {
          border-color: #6b1122; background: rgba(107,17,34,0.05);
        }
        /* ── CALENDAR WRAPPER: desktop side-by-side ── */
        .ev-cal-wrapper {
          background: #fff; border-radius: 12px; border: 1px solid #e0ddd8;
          box-shadow: 0 8px 32px rgba(0,0,0,0.06); overflow: hidden;
        }
        .ev-cal-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 28px; background: #6b1122; color: #fff;
        }
        .ev-cal-header h2 {
          font-family: 'Bebas Neue','Impact',sans-serif;
          font-size: 1.6rem; letter-spacing: 0.05em; margin: 0;
        }
        .ev-cal-nav {
          background: rgba(255,255,255,0.15); border: none; color: #fff;
          width: 36px; height: 36px; border-radius: 50%; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.2rem; transition: background 0.2s; line-height: 1;
        }
        .ev-cal-nav:hover { background: rgba(255,255,255,0.3); }
        .ev-cal-legend {
          display: flex; gap: 18px; padding: 12px 28px;
          background: #f9f7f4; border-bottom: 1px solid #ece8e2;
          flex-wrap: wrap;
        }
        .ev-cal-legend-item {
          display: flex; align-items: center; gap: 6px;
          font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.05em; color: #555;
        }
        .ev-cal-legend-dot { width: 10px; height: 10px; border-radius: 50%; }

        /* Body: side-by-side on desktop */
        .ev-cal-body {
          display: flex;
        }
        .ev-cal-left {
          flex: 1; min-width: 0; border-right: 1px solid #ece8e2;
        }
        .ev-cal-right {
          width: 300px; flex-shrink: 0;
          background: #fdf8f9; display: flex; flex-direction: column;
          overflow-y: auto;
        }

        .ev-cal-grid-header {
          display: grid; grid-template-columns: repeat(7, 1fr);
          background: #f2ede8; border-bottom: 1px solid #e0ddd8;
        }
        .ev-cal-dow {
          padding: 10px 0; text-align: center;
          font-size: 0.65rem; font-weight: 800; color: #888;
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .ev-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
        .ev-cal-cell {
          min-height: 72px; padding: 8px 6px;
          border-right: 1px solid #f0ece8; border-bottom: 1px solid #f0ece8;
          cursor: pointer; transition: background 0.15s; position: relative;
          display: flex; flex-direction: column; align-items: center;
        }
        .ev-cal-cell:nth-child(7n) { border-right: none; }
        .ev-cal-cell.empty { background: #faf9f7; cursor: default; }
        .ev-cal-cell.has-events:hover { background: #fdf5f6; }
        .ev-cal-cell.selected { background: #fdf0f2 !important; box-shadow: inset 0 0 0 2px #6b1122; }
        .ev-cal-cell.hoje .ev-cal-day-num {
          background: #006437; color: #fff; border-radius: 50%;
          width: 26px; height: 26px; display: flex; align-items: center;
          justify-content: center; font-weight: 900;
        }
        .ev-cal-day-num {
          font-size: 0.82rem; font-weight: 700; color: #333;
          margin-bottom: 4px; min-width: 26px; text-align: center;
          line-height: 26px;
        }
        .ev-cal-dots {
          display: flex; flex-wrap: wrap; justify-content: center; gap: 3px;
          max-width: 60px;
        }
        .ev-cal-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

        /* Right panel */
        .ev-day-panel {
          padding: 24px 20px; flex: 1;
          animation: evFadeIn 0.2s ease;
        }
        .ev-day-panel-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; flex: 1; padding: 32px 20px;
          text-align: center; color: #aaa;
        }
        .ev-day-panel-empty svg { margin-bottom: 12px; opacity: 0.35; }
        .ev-day-panel-empty p { font-size: 0.78rem; font-weight: 700; line-height: 1.5; }
        @keyframes evFadeIn {
          from { opacity: 0; transform: translateX(6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .ev-day-panel-title {
          font-size: 0.85rem; font-weight: 900; color: #6b1122;
          text-transform: uppercase; letter-spacing: 0.06em;
          margin-bottom: 14px; padding-bottom: 10px;
          border-bottom: 1px solid #ece8e2;
        }
        .ev-day-event-list { display: flex; flex-direction: column; gap: 10px; }
        .ev-day-event-item {
          display: flex; gap: 10px; align-items: flex-start;
          background: #fff; border-radius: 8px; padding: 12px 14px;
          border: 1px solid #ece8e2; text-decoration: none; color: inherit;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .ev-day-event-item:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.08); transform: translateX(2px);
        }
        .ev-day-event-badge {
          flex-shrink: 0; padding: 3px 8px; border-radius: 4px;
          font-size: 0.55rem; font-weight: 800; text-transform: uppercase;
          letter-spacing: 0.05em; color: #fff; margin-top: 2px; white-space: nowrap;
        }
        .ev-day-event-info { flex: 1; }
        .ev-day-event-titulo {
          font-weight: 800; font-size: 0.82rem; color: #222;
          margin-bottom: 3px; line-height: 1.3;
        }
        .ev-day-event-meta { font-size: 0.7rem; color: #777; font-weight: 600; }
        .ev-day-event-hora {
          font-size: 0.72rem; font-weight: 800; color: #6b1122;
          flex-shrink: 0; margin-top: 2px;
        }

        /* Mobile: stack calendar + panel */
        @media (max-width: 768px) {
          .ev-cal-body { flex-direction: column; }
          .ev-cal-left { border-right: none; }
          .ev-cal-right { width: 100%; }
          .ev-day-panel { border-top: 2px solid #6b1122; }
          .ev-day-panel-empty { display: none; }
          .ev-cal-cell { min-height: 50px; padding: 5px 2px; }
          .ev-cal-header { padding: 14px 16px; }
          .ev-cal-header h2 { font-size: 1.2rem; }
          .ev-cal-legend { padding: 8px 14px; gap: 12px; }
          .ev-day-panel { padding: 16px; }
          .ev-cal-dow { font-size: 0.55rem; padding: 7px 0; }
          .ev-cal-dot { width: 6px; height: 6px; }
        }
      `}</style>

      {/* Botões de alternância */}
      <div className="ev-toggle-bar">
        <button
          id="btn-view-cards"
          className={`ev-toggle-btn${view === 'cards' ? ' active' : ''}`}
          onClick={() => setView('cards')}
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
              d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          Cards
        </button>
        <button
          id="btn-view-calendar"
          className={`ev-toggle-btn${view === 'calendar' ? ' active' : ''}`}
          onClick={() => setView('calendar')}
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Calendário
        </button>
      </div>

      {/* ── VISTA EM CARDS ── */}
      {view === 'cards' && (
        <div className="cgt-eventos-grid">
          {eventos.map((evento) => (
            <a href={`/eventos/${evento.id}`} key={evento.id} className="cgt-evento-card">
              <div className="cgt-evento-thumb">
                <img
                  src={evento.banner_url || 'https://static.vecteezy.com/ti/fotos-gratis/t1/8641767-centro-de-campo-de-futebol-fundo-esporte-jogo-e-conceito-atletico-renderizacao-ilustracao-em-3d-foto.jpg'}
                  alt={evento.titulo}
                />
              </div>
              <div className="cgt-evento-body">
                <div className="cgt-evento-meta">
                  <span className="cgt-evento-tipo">{evento.tipo}</span>
                  <span className="cgt-evento-data">
                    {new Date(evento.data_evento).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <h4 className="cgt-evento-titulo">{evento.titulo}</h4>
                <p className="cgt-evento-desc">{evento.descricao}</p>
                <div className="cgt-evento-local">📍 {evento.local || 'Local não informado'}</div>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* ── VISTA EM CALENDÁRIO ── */}
      {view === 'calendar' && (
        <div className="ev-cal-wrapper">
          {/* Cabeçalho com navegação */}
          <div className="ev-cal-header">
            <button className="ev-cal-nav" onClick={prevMes} id="btn-cal-prev" aria-label="Mês anterior">‹</button>
            <h2>{MESES[calMes].toUpperCase()} {calAno}</h2>
            <button className="ev-cal-nav" onClick={nextMes} id="btn-cal-next" aria-label="Próximo mês">›</button>
          </div>

          {/* Legenda de cores por tipo */}
          <div className="ev-cal-legend">
            {Object.entries(TIPO_LABEL).map(([tipo, label]) => (
              <div key={tipo} className="ev-cal-legend-item">
                <div className="ev-cal-legend-dot" style={{ background: TIPO_COLOR[tipo] }} />
                {label}
              </div>
            ))}
          </div>

          {/* Body: grade + painel lado a lado no desktop */}
          <div className="ev-cal-body">

            {/* Coluna esquerda: grade do calendário */}
            <div className="ev-cal-left">
              <div className="ev-cal-grid-header">
                {DIAS_SEMANA.map(d => (
                  <div key={d} className="ev-cal-dow">{d}</div>
                ))}
              </div>
              <div className="ev-cal-grid">
                {cells.map((dia, idx) => {
                  if (dia === null) {
                    return <div key={`empty-${idx}`} className="ev-cal-cell empty" />;
                  }
                  const evsDia = getEventosDoDia(eventos, calAno, calMes, dia);
                  const isHoje =
                    hoje.getFullYear() === calAno &&
                    hoje.getMonth() === calMes &&
                    hoje.getDate() === dia;
                  const isSelected = diaSelecionado === dia;
                  const hasEvents = evsDia.length > 0;

                  return (
                    <div
                      key={dia}
                      className={[
                        'ev-cal-cell',
                        isHoje ? 'hoje' : '',
                        isSelected ? 'selected' : '',
                        hasEvents ? 'has-events' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => {
                        if (hasEvents) setDiaSelecionado(isSelected ? null : dia);
                      }}
                      title={hasEvents ? `${evsDia.length} evento(s) neste dia` : undefined}
                    >
                      <div className="ev-cal-day-num">{dia}</div>
                      {hasEvents && (
                        <div className="ev-cal-dots">
                          {evsDia.slice(0, 4).map((e, i) => (
                            <div
                              key={i}
                              className="ev-cal-dot"
                              style={{ background: TIPO_COLOR[e.tipo] || '#888' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Coluna direita: painel de detalhes (sempre visível no desktop) */}
            <div className="ev-cal-right">
              {diaSelecionado && eventosDoDiaSelecionado.length > 0 ? (
                <div className="ev-day-panel">
                  <div className="ev-day-panel-title">
                    {diaSelecionado} de {MESES[calMes]} de {calAno}
                  </div>
                  <div className="ev-day-event-list">
                    {eventosDoDiaSelecionado.map(e => {
                      const data = new Date(e.data_evento);
                      const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                      return (
                        <a href={`/eventos/${e.id}`} key={e.id} className="ev-day-event-item">
                          <div
                            className="ev-day-event-badge"
                            style={{ background: TIPO_COLOR[e.tipo] || '#888' }}
                          >
                            {TIPO_LABEL[e.tipo] || e.tipo}
                          </div>
                          <div className="ev-day-event-info">
                            <div className="ev-day-event-titulo">{e.titulo}</div>
                            <div className="ev-day-event-meta">📍 {e.local || 'Local não informado'}</div>
                          </div>
                          <div className="ev-day-event-hora">{hora}</div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="ev-day-panel-empty">
                  <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="#6b1122">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>Clique em um dia com eventos para ver os detalhes aqui</p>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
}
